import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface ClientMountContextType {
  mounted: boolean;
}

const ClientMountContext = createContext<ClientMountContextType | undefined>(undefined);

/**
 * ClientMountProvider will only render its children after we observe a usable
 * browser window size. This prevents components that read window dimensions
 * (or rely on ViewSizeContext) from mounting with viewWidth === 0.
 * During SSR/SSG, it renders children immediately so HTML is generated.
 * During client-side hydration, it waits for window dimensions before rendering.
 */
export const ClientMountProvider: React.FC<{ children: ReactNode }> = ({ children }) => {

  // const [mounted, setMounted] = useState(false); // SSR Fix: Old -Gabe

  // On server (SSR/SSG), start as true so HTML is generated
  // On client, start as false so we wait for real window dimensions
  const [mounted, setMounted] = useState(false); // SSR Fix: New -Gabe

  // -------------------- Effect: Detect usable window size

  useEffect(() => {
    let rafId: number | null = null;
    let timeoutId: number | null = null;

    const tryMount = () => {
      // Some browsers may briefly report 0 during early hydration/layout.
      // Wait until innerWidth is a positive number. If it doesn't become
      // available quickly, fallback to mounting after a short timeout.
      if (window.innerWidth && window.innerWidth > 0) {
        setMounted(true);
      } else {
        rafId = window.requestAnimationFrame(tryMount);
        // safety fallback: ensure we don't poll forever
        if (timeoutId == null) {
          timeoutId = window.setTimeout(() => {
            setMounted(true);
          }, 300);
        }
      }
    };

    tryMount();

    return () => {
      if (rafId != null) window.cancelAnimationFrame(rafId);
      if (timeoutId != null) window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <ClientMountContext.Provider value={{ mounted }}>
      {children}
    </ClientMountContext.Provider>
  );
};

export const useClientMount = (): ClientMountContextType => {
  const ctx = useContext(ClientMountContext);
  return ctx || { mounted: false };
};
